import type { StreamSnapshot } from "./types";
import type { Message } from "@langchain/langgraph-sdk";

/** Returns true if the message has visible text content (not just tool calls). */
function hasVisibleText(msg: Message): boolean {
  if (msg.type === "tool") return false;
  const content = msg.content;
  if (typeof content === "string") return content.length > 0;
  if (Array.isArray(content)) {
    return content.some((block: any) => block?.type === "text" && block.text);
  }
  return false;
}

/**
 * Pre-built selectors for common stream data patterns.
 *
 * Using these with useStreamSelector enables granular subscriptions —
 * components only re-render when their specific data changes.
 *
 * @example
 * ```tsx
 * const messages = useStreamSelector(StreamSelectors.messages);
 * const isLoading = useStreamSelector(StreamSelectors.isLoading);
 * ```
 */
export const StreamSelectors = {
  /** All messages from the stream */
  messages: (s: StreamSnapshot) => s.messages,

  /** Only messages with visible text content (excludes tool calls) */
  textMessages: (s: StreamSnapshot) => s.messages.filter(hasVisibleText),

  /** The current state values */
  values: (s: StreamSnapshot) => s.values,

  /** Whether the stream is currently running */
  isLoading: (s: StreamSnapshot) => s.isLoading,

  /** Whether the thread's history is currently being loaded */
  isThreadLoading: (s: StreamSnapshot) => s.isThreadLoading,

  /** Alias for isThreadLoading — loading prior thread history */
  isLoadingHistory: (s: StreamSnapshot) => s.isThreadLoading,

  /** Last seen error — transport / SDK-level (request failure, connection drop, etc.) */
  error: (s: StreamSnapshot) => s.error,

  /**
   * Structured error reported by the agent's harness, distinct from the
   * SDK transport error above. The langgraph agents (coding, brainstorm,
   * ads, support) populate `state.error = { message, node, kind }` for
   * every non-bubbleUp failure their error boundary catches. The SDK
   * request succeeds — only the in-state error field carries the signal
   * — so consumers need a dedicated selector.
   *
   * `kind` is one of:
   *   - `"agent_fixable"` — recursion limit, malformed update. A
   *     synthetic visible recovery AIMessage is already in the chat.
   *   - `"transient"` — Anthropic 529 / 5xx / network drop. Same: recovery
   *     message visible in chat.
   *   - `"harness"` — our server code threw. NO synthetic agent
   *     message; the banner is the only signal the user gets.
   *
   * A checkpoint written before a deploy can still carry the older
   * `"agentRuntime"`/`"provider"` spellings; the banner treats them as
   * `agent_fixable`/`transient`.
   *
   * See `app/core/harness/errorBoundary.ts` for where this is populated.
   */
  stateError: (s: StreamSnapshot) =>
    (
      s.values as
        | {
            error?: {
              message: string;
              node: string;
              kind: "agent_fixable" | "transient" | "harness" | "agentRuntime" | "provider";
            };
          }
        | undefined
    )?.error,

  /** Interrupt value if interrupted */
  interrupt: (s: StreamSnapshot) => s.interrupt,

  /** Tool calls paired with their results */
  toolCalls: (s: StreamSnapshot) => s.toolCalls,

  /** Current branch */
  branch: (s: StreamSnapshot) => s.branch,

  /** Flattened history of thread states */
  history: (s: StreamSnapshot) => s.history,

  /** Submit function */
  submit: (s: StreamSnapshot) => s.submit,

  /** Stop function */
  stop: (s: StreamSnapshot) => s.stop,

  /** Whether the stream is actively streaming (alias for isLoading) */
  isStreaming: (s: StreamSnapshot) => s.isLoading,

  /** Whether the stream is ready (not loading, no error) */
  isReady: (s: StreamSnapshot) => !s.isLoading && !s.error,
} as const;
